import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadPdfFonts } from '../loadPdfFonts';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, HeadingLevel, AlignmentType, ImageRun, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

interface CheckItem {
  id: number;
  category: string;
  item_key: string;
  item_label_gr: string;
  item_label_en: string;
  status: string;
  note: string;
  item_order: number;
  is_custom: boolean;
}

interface CheckData {
  id: number;
  vessel_id: number;
  vessel_name: string;
  check_date: string;
  technician_name: string;
  technician_code: string;
  signature_image: string;
  summary_ok: number;
  summary_not_ok: number;
  items?: CheckItem[];
}

const RoutineCheckView: React.FC = () => {
  const navigate = useNavigate();
  const { checkId } = useParams<{ checkId: string }>();
  const [lang, setLang] = useState<'el' | 'en'>(
    (sessionStorage.getItem('yacht_lang') as 'el' | 'en') || 'el'
  );
  const [check, setCheck] = useState<CheckData | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  const currentUser = JSON.parse(sessionStorage.getItem('auth_current_user') || '{}');
  const canDelete = currentUser.role === 'ADMIN' || currentUser.role === 'TECHNICAL';

  useEffect(() => {
    sessionStorage.setItem('yacht_lang', lang);
  }, [lang]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`/api/routine-checks.php?id=${checkId}`);
        if (r.ok) {
          const d = await r.json();
          setCheck(d.check || d);
        }
      } catch (e) {
        console.error('Error loading check:', e);
      } finally {
        setLoading(false);
      }
    };
    if (checkId) load();
  }, [checkId]);

  const t = (el: string, en: string) => (lang === 'el' ? el : en);

  const fmtDate = (iso: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleDelete = async () => {
    if (!window.confirm(t('Σίγουρα διαγραφή;', 'Confirm delete?'))) return;
    setDeleting(true);
    try {
      const r = await fetch(`/api/routine-checks.php?id=${checkId}`, { method: 'DELETE' });
      if (r.ok) {
        alert(t('Διαγράφηκε!', 'Deleted!'));
        navigate(-1);
      } else {
        alert(t('Σφάλμα διαγραφής', 'Delete failed'));
      }
    } catch (e) {
      alert(t('Σφάλμα διαγραφής', 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  // ============ EXPORT: PDF ============
  const exportPDF = async () => {
    if (!check) return;
    setExporting('pdf');
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });

      // Load DejaVuSans font for Unicode support (Greek, Russian, Hebrew, etc.)
      await loadPdfFonts();
      if ((window as any).DejaVuSansBase64) {
        doc.addFileToVFS('DejaVuSans.ttf', (window as any).DejaVuSansBase64);
        doc.addFont('DejaVuSans.ttf', 'DejaVuSans', 'normal');
      }
      if ((window as any).DejaVuSansBoldBase64) {
        doc.addFileToVFS('DejaVuSans-Bold.ttf', (window as any).DejaVuSansBoldBase64);
        doc.addFont('DejaVuSans-Bold.ttf', 'DejaVuSans', 'bold');
      }
      doc.setFont('DejaVuSans', 'normal');

      const pageW = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(16);
      doc.setFont('DejaVuSans', 'bold');
      doc.setTextColor(30, 64, 175);
      doc.text('ROUTINE CHECK REPORT', pageW / 2, 18, { align: 'center' });

      // Meta
      doc.setFontSize(10);
      doc.setFont('DejaVuSans', 'normal');
      doc.setTextColor(55, 65, 81);
      doc.text(`Vessel: ${check.vessel_name || '-'}`, 14, 30);
      doc.text(`Date: ${fmtDate(check.check_date)}`, 14, 36);
      doc.text(`Technician: ${check.technician_name || '-'}`, 14, 42);
      doc.text(`OK: ${check.summary_ok || 0}    NOT OK: ${check.summary_not_ok || 0}`, 14, 48);

      // Items table
      const items = check.items || [];
      const tableData = items.map((i, idx) => [
        idx + 1,
        i.category,
        lang === 'el' ? (i.item_label_gr || i.item_label_en) : (i.item_label_en || i.item_label_gr),
        i.status,
        i.note || '-',
      ]);

      autoTable(doc, {
        startY: 55,
        head: [['#', 'Category', 'Item', 'Status', 'Note']],
        body: tableData,
        theme: 'striped',
        styles: { font: 'DejaVuSans' },
        headStyles: { fillColor: [30, 64, 175], textColor: 255, fontSize: 9, font: 'DejaVuSans', fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, font: 'DejaVuSans', fontStyle: 'normal' },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 70 },
          3: { cellWidth: 20 },
          4: { cellWidth: 'auto' },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 3) {
            const status = String(data.cell.raw);
            if (status === 'OK') {
              data.cell.styles.fillColor = [220, 252, 231];
              data.cell.styles.textColor = [21, 128, 61];
            } else if (status === 'NOT_OK') {
              data.cell.styles.fillColor = [254, 226, 226];
              data.cell.styles.textColor = [185, 28, 28];
            }
          }
        },
      });

      // Signature
      const finalY = (doc as any).lastAutoTable.finalY || 100;
      let sigY = finalY + 10;
      if (sigY > 240) {
        doc.addPage();
        sigY = 20;
      }
      doc.setFontSize(11);
      doc.setFont('DejaVuSans', 'bold');
      doc.setTextColor(55, 65, 81);
      doc.text('Technician Signature:', 14, sigY);
      if (check.signature_image && check.signature_image.startsWith('data:image')) {
        try {
          doc.addImage(check.signature_image, 'PNG', 14, sigY + 4, 80, 30);
        } catch (e) {
          doc.text('(signature not rendered)', 14, sigY + 10);
        }
      }
      doc.setFontSize(9);
      doc.setFont('DejaVuSans', 'normal');
      doc.text(fmtDate(check.check_date), 14, sigY + 40);

      doc.save(`routine-check-${check.id}-${check.vessel_name || 'vessel'}.pdf`);
    } catch (e) {
      console.error('PDF error:', e);
      alert(t('Σφάλμα PDF', 'PDF error'));
    } finally {
      setExporting(null);
    }
  };

  // ============ EXPORT: Word ============
  const exportWord = async () => {
    if (!check) return;
    setExporting('word');
    try {
      const items = check.items || [];

      const headerCells = ['#', 'Category', 'Item', 'Status', 'Note'].map(h => new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, color: 'FFFFFF' })] })],
        shading: { fill: '1E40AF' },
      }));

      const rows = items.map((i, idx) => new TableRow({
        children: [
          new TableCell({ children: [new Paragraph(String(idx + 1))] }),
          new TableCell({ children: [new Paragraph(i.category)] }),
          new TableCell({ children: [new Paragraph(lang === 'el' ? (i.item_label_gr || i.item_label_en) : (i.item_label_en || i.item_label_gr))] }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: i.status, color: i.status === 'OK' ? '15803D' : 'B91C1C', bold: true })] })],
            shading: { fill: i.status === 'OK' ? 'DCFCE7' : 'FEE2E2' },
          }),
          new TableCell({ children: [new Paragraph(i.note || '-')] }),
        ],
      }));

      const docChildren: any[] = [
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          children: [new TextRun({ text: 'ROUTINE CHECK REPORT', bold: true, color: '1E40AF', size: 32 })],
        }),
        new Paragraph({ children: [new TextRun({ text: `Vessel: ${check.vessel_name || '-'}`, bold: true })] }),
        new Paragraph({ children: [new TextRun(`Date: ${fmtDate(check.check_date)}`)] }),
        new Paragraph({ children: [new TextRun(`Technician: ${check.technician_name || '-'}`)] }),
        new Paragraph({ children: [new TextRun(`OK: ${check.summary_ok || 0}    NOT OK: ${check.summary_not_ok || 0}`)] }),
        new Paragraph(''),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({ children: headerCells }), ...rows],
        }),
        new Paragraph(''),
        new Paragraph({ children: [new TextRun({ text: 'Technician Signature:', bold: true })] }),
      ];

      // Add signature image
      if (check.signature_image && check.signature_image.startsWith('data:image')) {
        try {
          const base64 = check.signature_image.split(',')[1];
          const binary = atob(base64);
          const len = binary.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
          docChildren.push(new Paragraph({
            children: [new ImageRun({
              data: bytes,
              transformation: { width: 200, height: 80 },
            } as any)],
          }));
        } catch (e) {
          docChildren.push(new Paragraph('(signature not rendered)'));
        }
      }
      docChildren.push(new Paragraph({ children: [new TextRun({ text: fmtDate(check.check_date), italics: true })] }));

      const wordDoc = new Document({ sections: [{ children: docChildren }] });
      const blob = await Packer.toBlob(wordDoc);
      saveAs(blob, `routine-check-${check.id}-${check.vessel_name || 'vessel'}.docx`);
    } catch (e) {
      console.error('Word error:', e);
      alert(t('Σφάλμα Word', 'Word error'));
    } finally {
      setExporting(null);
    }
  };

  // ============ EXPORT: Excel ============
  const exportExcel = () => {
    if (!check) return;
    setExporting('excel');
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summary = [
        ['ROUTINE CHECK REPORT'],
        [],
        ['Vessel', check.vessel_name || '-'],
        ['Date', fmtDate(check.check_date)],
        ['Technician', check.technician_name || '-'],
        ['OK', check.summary_ok || 0],
        ['NOT OK', check.summary_not_ok || 0],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Items
      const items = check.items || [];
      const itemsData = [
        ['#', 'Category', 'Item (GR)', 'Item (EN)', 'Status', 'Note'],
        ...items.map((i, idx) => [
          idx + 1,
          i.category,
          i.item_label_gr || '',
          i.item_label_en || '',
          i.status,
          i.note || '',
        ]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(itemsData);
      XLSX.utils.book_append_sheet(wb, ws2, 'Items');

      XLSX.writeFile(wb, `routine-check-${check.id}-${check.vessel_name || 'vessel'}.xlsx`);
    } catch (e) {
      console.error('Excel error:', e);
      alert(t('Σφάλμα Excel', 'Excel error'));
    } finally {
      setExporting(null);
    }
  };

  // ============ PRINT ============
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="min-h-screen bg-[#f3f4f6] p-8 text-center text-gray-500">{t('Φόρτωση...', 'Loading...')}</div>;
  }

  if (!check) {
    return <div className="min-h-screen bg-[#f3f4f6] p-8 text-center text-gray-500">{t('Δεν βρέθηκε', 'Not found')}</div>;
  }

  const items = check.items || [];
  const externalItems = items.filter(i => i.category === 'EXTERNAL');
  const internalItems = items.filter(i => i.category === 'INTERNAL');

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      <div className="bg-[#1e40af] p-4 print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-[#1e3a8a] rounded-lg transition-colors">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">
                📋 {t('Έλεγχος', 'Check')} #{check.id}
              </h1>
              <p className="text-[#dbeafe] text-sm">
                🛥️ {check.vessel_name || `Vessel #${check.vessel_id}`}
              </p>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === 'el' ? 'en' : 'el')}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm text-white transition-colors"
          >
            {lang === 'el' ? 'EN' : 'EL'}
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Export buttons */}
        <div className="bg-white rounded-lg shadow p-3 mb-4 flex flex-wrap gap-2 justify-center print:hidden">
          <button
            onClick={exportPDF}
            disabled={exporting !== null}
            className="px-4 py-2 bg-white hover:bg-red-50 text-red-600 border-2 border-red-400 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
          >
            {exporting === 'pdf' ? '⏳' : '📄'} PDF
          </button>
          <button
            onClick={exportWord}
            disabled={exporting !== null}
            className="px-4 py-2 bg-white hover:bg-blue-50 text-blue-600 border-2 border-blue-400 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
          >
            {exporting === 'word' ? '⏳' : '📝'} Word
          </button>
          <button
            onClick={exportExcel}
            disabled={exporting !== null}
            className="px-4 py-2 bg-white hover:bg-green-50 text-green-600 border-2 border-green-400 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
          >
            {exporting === 'excel' ? '⏳' : '📊'} Excel
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-400 rounded-lg text-sm font-bold transition-all"
          >
            🖨️ {t('Εκτύπωση', 'Print')}
          </button>
        </div>

        {/* Meta */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-[#6b7280]">{t('Ημερομηνία', 'Date')}</div>
            <div className="font-semibold text-[#374151]">{fmtDate(check.check_date)}</div>
          </div>
          <div>
            <div className="text-xs text-[#6b7280]">{t('Τεχνικός', 'Technician')}</div>
            <div className="font-semibold text-[#374151]">{check.technician_name || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-[#6b7280]">{t('Σύνοψη', 'Summary')}</div>
            <div>
              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold mr-2">
                OK: {check.summary_ok || 0}
              </span>
              <span className="inline-block px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                NOT OK: {check.summary_not_ok || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-bold mb-3 text-[#374151]">
              {t('ΕΞΩΤΕΡΙΚΑ', 'EXTERNAL')}
            </h2>
            {externalItems.length === 0 ? (
              <p className="text-sm text-gray-500">{t('Καμία εγγραφή', 'No items')}</p>
            ) : (
              externalItems.map(i => (
                <div key={i.id} className="flex justify-between items-start py-2 border-b border-[#e5e7eb] last:border-0">
                  <div className="flex-1">
                    <div className="text-sm text-[#374151]">{lang === 'el' ? i.item_label_gr : i.item_label_en}</div>
                    {i.note && (
                      <div className="text-xs text-[#6b7280] mt-1 italic">📝 {i.note}</div>
                    )}
                  </div>
                  <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${i.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {i.status === 'OK' ? '✅ OK' : '❌ NOT OK'}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-bold mb-3 text-[#374151]">
              {t('ΕΣΩΤΕΡΙΚΑ', 'INTERNAL')}
            </h2>
            {internalItems.length === 0 ? (
              <p className="text-sm text-gray-500">{t('Καμία εγγραφή', 'No items')}</p>
            ) : (
              internalItems.map(i => (
                <div key={i.id} className="flex justify-between items-start py-2 border-b border-[#e5e7eb] last:border-0">
                  <div className="flex-1">
                    <div className="text-sm text-[#374151]">{lang === 'el' ? i.item_label_gr : i.item_label_en}</div>
                    {i.note && (
                      <div className="text-xs text-[#6b7280] mt-1 italic">📝 {i.note}</div>
                    )}
                  </div>
                  <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${i.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {i.status === 'OK' ? '✅ OK' : '❌ NOT OK'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Signature */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 max-w-2xl mx-auto">
          <h3 className="text-lg font-bold mb-3 text-center text-[#374151]">
            {t('Υπογραφή Τεχνικού', 'Technician Signature')}
          </h3>
          <div className="border-2 border-[#d1d5db] rounded-lg bg-white flex justify-center p-2">
            {check.signature_image ? (
              <img src={check.signature_image} alt="signature" className="max-h-40" />
            ) : (
              <p className="text-sm text-gray-500 py-8">{t('Χωρίς υπογραφή', 'No signature')}</p>
            )}
          </div>
          <p className="text-center text-[#374151] mt-3 text-sm font-semibold">
            {fmtDate(check.check_date)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-3 print:hidden">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-[#6b7280] hover:bg-[#4b5563] text-white rounded-lg transition-colors"
          >
            {t('Πίσω', 'Back')}
          </button>
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {deleting ? t('Διαγραφή...', 'Deleting...') : '🗑️ ' + t('Διαγραφή', 'Delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutineCheckView;
